import React, { useEffect, useState, useMemo } from 'react';
import { IPointsDetail, IPointsParams } from '../../interface/student';
import { defaultPointsDetail } from '../../server/defaultData/student';
import { If, Loading } from 'common/src/components';
import isFunction from 'lodash/isFunction';
import kebabCase from 'lodash/kebabCase';
import get from 'lodash/get';
import { Button, Modal, Radio, InputNumber } from '@umu/umu-design';
import { lang } from '@umu/ukit-lang';
import { CooperationActionWrap, ICooperationRole } from '../../../cooperationActionWrap';
import { UserAvatar, UserName } from '../../../user';

import isNumber from 'lodash/isNumber';
import { studentPointsServer } from '../../server';
interface IPointsItem {
    key: string;
    text: string;
    isHideTag?: boolean;
}
interface IProps {
    info: Record<string, any>;
    config: {
        groupId: string;
        sessionId: string;
        role?: ICooperationRole;
        editorPointsLabel?: string;
        isAllowEditorPoints?: boolean;
        pointSplit?: string | JSX.Element;
        pointsList: IPointsItem[];
        pointsHideList?: string[];
        actionName?: string;
        title: string | JSX.Element | ((val: IPointsDetail) => JSX.Element);
        onSuccessCallback?: (points: number) => void;
    };
}
const { confirm } = Modal;
const { pointsDetail, setPointsCount } = studentPointsServer;
const UserPointDetail = (props: IProps) => {
    const { info, config } = props;
    const {
        groupId,
        sessionId,
        title = '',
        pointsList = [],
        pointSplit = '',
        isAllowEditorPoints = false,
        editorPointsLabel = '',
        actionName = '',
        onSuccessCallback,
    } = config;
    const [isLoading, setIsLoading] = useState(true);
    const [pointInfo, setPointInfo] = useState<IPointsDetail>({ ...defaultPointsDetail });
    const init = async () => {
        const params = {
            group_id: groupId,
            session_id: sessionId,
            // 未登录用户的 student_id 都是 0，对于未登录用户查完成情况应该用 user_id （访客ID）
            student_id: info?.student_id === 0 ? info?.user_id : info?.student_id,
        };
        const data = await pointsDetail(params);
        setPointInfo(data);
        setIsLoading(false);
    };
    useEffect(() => {
        init();
    }, []);
    const onRenderTitle = () => {
        const isFunc = isFunction(title);
        return <div className="points-detail-title">{isFunc ? title(pointInfo) : title}</div>;
    };
    const onRenderPointsList = () => {
        const list = pointsList.filter((item) => {
            const { key } = item;
            return !config.pointsHideList?.includes(key);
        });
        return list.map((item) => {
            const { key, text } = item;
            const pointValue = Number((pointInfo as any)[key] || '0');
            const itemCls = !!pointValue ? kebabCase(key) : 'disabled';
            return (
                <li key={key} className="points-detail-item">
                    <div className="points-detail-item-text">
                        <If condition={!item?.isHideTag}>
                            <span className={`points-detail-item-tag points-detail-item-tag-${itemCls}`} />
                        </If>
                        {text}
                    </div>
                    <div className="points-detail-item-value">
                        {pointSplit}
                        <span className={`value-info ${pointSplit ? 'value-info-split' : ''}`}>
                            {(pointInfo as any)[key] || '0'}
                        </span>
                    </div>
                </li>
            );
        });
    };
    const onRenderUserAvatar = () => {
        return (
            <div className="custom-table-student-points-user-info">
                <UserAvatar
                    src={info.avatar}
                    width={32}
                    className="custom-table-student-points-user-info-user-avatar"
                    showUserLevel={Number(get(info, 'show_user_level'))}
                    userLevelInfo={Number(get(info, 'user_level'))}
                />
                <UserName
                    name={info.user_name}
                    className="custom-table-student-points-user-info-user-name"
                    userMedalInfo={get(info, 'user_medal')}
                    showUserLevel={get(info, 'show_user_level')}
                    userMedalWidth={20}
                    transcate={true}
                />
            </div>
        );
    };
    const onPointsChangeSuccessCallback = (points: number) => {
        const newPointInfo = Object.assign({}, pointInfo, {
            special_recognition_rank: pointInfo.special_recognition_rank + points,
        });
        setPointInfo(newPointInfo);
        onSuccessCallback && onSuccessCallback(points);
    };
    const onEditorPoints = () => {
        let enabledMinusScore = Number(pointInfo?.special_approve_points) || 0;
        enabledMinusScore = isNumber(enabledMinusScore) ? enabledMinusScore : 0;
        const dialog = confirm({
            title: lang('修改特别认可积分'),
            width: '640px',
            className: 'editor-user-points-dialog-wrapper',
            content: (
                <EditorUserPoint
                    params={{
                        group_id: groupId,
                        session_id: sessionId,
                        // 未登录用户的 student_id 都是 0，对于未登录用户查完成情况应该用 user_id （访客ID）
                        student_id: info?.student_id === 0 ? info?.user_id : info?.student_id,
                    }}
                    enabledMinusScore={enabledMinusScore}
                    heard={onRenderUserAvatar()}
                    onSuccessCallback={onPointsChangeSuccessCallback}
                    onClose={() => dialog.destroy()}
                />
            ),
            centered: true,
        });
    };
    return (
        <div className="render-custom-user-points-detail">
            <If condition={isLoading}>
                <Loading className="points-detail-loading" />
            </If>
            <If condition={!isLoading}>
                <div>
                    <If condition={isAllowEditorPoints}>
                        <CooperationActionWrap role={config.role} actionName={actionName} cls="points-detail-heard">
                            <Button type="text" className="points-editor-btn" isOnlyChildren onClick={onEditorPoints}>
                                {editorPointsLabel}
                            </Button>
                        </CooperationActionWrap>
                    </If>
                    {onRenderUserAvatar()}
                    {onRenderTitle()}
                    <ul className="points-detail-list">{onRenderPointsList()}</ul>
                </div>
            </If>
        </div>
    );
};
enum OP_TYPE {
    PLUS,
    MINUS,
}
const pointOperateList = [
    {
        key: String(OP_TYPE.PLUS),
        label: lang('加分'),
    },
    {
        key: String(OP_TYPE.MINUS),
        label: lang('减分'),
    },
];
interface IEditorUserPointProps {
    heard: JSX.Element;
    onClose: VoidFunction;
    enabledMinusScore: number;
    params: IPointsParams;
    onSuccessCallback?: (points: number, data: Record<string, any>) => void;
}
const EditorUserPoint = (props: IEditorUserPointProps) => {
    const { heard, enabledMinusScore = 0, params, onClose, onSuccessCallback } = props;
    const [active, setActive] = useState<OP_TYPE>(OP_TYPE.PLUS);
    const [points, setPoints] = useState<number>(0);
    const onSubmit = async () => {
        const newParams = Object.assign({}, params, {
            point: points,
            is_minus: active,
        });
        const { data } = await setPointsCount(newParams);
        onClose();
        onSuccessCallback && onSuccessCallback(active === OP_TYPE.MINUS ? 0 - points : points, data);
    };
    const maxValue = useMemo(() => {
        return Number(active) === OP_TYPE.MINUS ? enabledMinusScore : 99999999; // 最大长度8
    }, [active]);
    return (
        <div className="editor-user-points-dialog">
            {heard}
            <div className="editor-user-points-dialog-content">
                <Radio
                    list={pointOperateList as any[]}
                    onChange={(key: string) => setActive(Number(key))}
                    active={String(active)}
                />

                <div className="points-operate">
                    <InputNumber
                        placeholder={lang('请输入')}
                        controlWidth={120}
                        maxLength={8}
                        max={maxValue}
                        min={0}
                        onChange={setPoints}
                    />
                    <span className="point-split">{lang('分')}</span>
                    <If condition={active === OP_TYPE.MINUS}>
                        <span className="points-enable-minus-score">{`${lang('可减积分')}: ${lang(
                            '{$0{0= 分,1= 分}}',
                            enabledMinusScore,
                        )}`}</span>
                    </If>
                </div>
                <div className="points-operate-desc">
                    {lang('您可以为学员加分，体现您对学员学习的认可。您也可以为学员减分，减分不能超过讲师的手动加分。')}
                </div>
            </div>
            <div className="editor-user-points-dialog-footer">
                <Button type="ghost-grey" className="btn-split" onClick={onClose}>
                    {lang('取消')}
                </Button>
                <Button type="primary" disabled={points <= 0} onClick={onSubmit}>
                    {lang('确认')}
                </Button>
            </div>
        </div>
    );
};
export { UserPointDetail };
